import { localAIEngine, AIResponse, IntentResult } from './local-ai-engine'

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
  timestamp?: number
}

export interface SessionInfo {
  sessionId: string
  createdAt: number
  lastAccessed: number
  messageCount: number
  isActive: boolean
}

export type ConversationState = 'idle' | 'greeting' | 'questioning' | 'processing' | 'answering' | 'summary'

export interface ConversationContext {
  state: ConversationState
  topic: string
  intentHistory: IntentResult[]
  slotAccumulator: Record<string, any>
  pendingActions: string[]
}

export interface HumorConfig {
  enableHumor: boolean
  humorProbability: number
  humorStyle: 'casual' | 'professional' | 'playful' | 'witty'
}

export class LocalAIService {
  private sessions: Map<string, {
    messages: ChatMessage[]
    createdAt: number
    lastAccessed: number
    context: ConversationContext
  }> = new Map()

  private maxHistoryLength = 10
  
  private humorConfig: HumorConfig = {
    enableHumor: true,
    humorProbability: 0.3,
    humorStyle: 'casual'
  }

  private humorResponseLibrary: Record<string, string[]> = {
    greet: [
      '嗨！我是谐伴AI，您的智能助手已上线！😊',
      '您好呀！今天有什么我可以帮您的吗？✨',
      '哈喽！准备好开始高效工作了吗？🚀',
      '你好！我是您的AI小伙伴，随时待命！😄',
      '嗨咯！今天天气不错，工作也要加油哦！☀️'
    ],
    help: [
      '别担心，我来帮您！有什么问题尽管问~ 🤝',
      '需要帮忙？我可是万能的AI助手哦！💪',
      '尽管吩咐，我会尽力为您排忧解难！🔧',
      '遇到难题了？让我来帮您分析分析！🧐',
      '好的，让我看看怎么帮您解决这个问题~ 🔍'
    ],
    search: [
      '收到！让我帮您找找看~ 🔍',
      '搜索开始！希望能快速找到您需要的内容~ ⚡',
      '正在帮您检索信息，请稍等片刻~ 📚',
      '搜索中... 相信很快就能找到答案！🚀',
      '好的，让我在知识库中帮您找找~ 🧠'
    ],
    report: [
      '生成报告中... 数据马上就来！📊',
      '正在为您整理数据，很快就能看到分析结果~ 📈',
      '报告生成中，请稍候片刻~ 📋',
      '让我来帮您分析这些数据，生成一份漂亮的报告！✨',
      '数据在说话，我来帮您解读~ 📉'
    ],
    login: [
      '登录问题？让我来帮您排查！🔐',
      '遇到登录困扰了？别着急，我们一起解决！🤔',
      '登录相关的问题，我都可以帮您处理~ 🔑',
      '好的，让我看看登录环节出了什么问题~ 👀',
      '登录是第一道门，我帮您把门打开！🚪'
    ],
    logout: [
      '好的，安全退出！下次见~ 👋',
      '再见啦！期待您下次再来~ 🌟',
      '已为您安全注销，祝您有个愉快的一天！😊',
      '拜拜~ 记得常回来看看哦！👀',
      '安全退出成功，下次见！✨'
    ],
    settings: [
      '配置问题？让我帮您调整！⚙️',
      '设置相关的问题，我都可以帮您解答~ 🛠️',
      '需要调整参数？尽管告诉我！🔧',
      '正在帮您查看配置选项~ ⚡',
      '设置是为了更好的体验，让我来帮您！🎛️'
    ],
    order: [
      '订单问题？我来帮您处理！📦',
      '正在帮您查看订单状态~ 🚚',
      '订单相关的查询，交给我！✅',
      '让我帮您追踪订单信息~ 🔍',
      '订单是财富的载体，让我帮您管理！💰'
    ],
    user: [
      '用户管理是我的强项！👥',
      '正在帮您查询用户信息~ 👤',
      '用户相关的操作，我可以帮您完成！👨‍💼',
      '让我来帮您管理用户数据~ 📊',
      '用户是上帝，我帮您服务好他们！🙏'
    ],
    finance: [
      '财务数据，我来帮您分析！💰',
      '正在帮您查看财务报表~ 📈',
      '财务相关的问题，尽管问我！💵',
      '让我帮您梳理一下财务状况~ 📉',
      '数字会说话，我帮您听懂！🔢'
    ],
    marketing: [
      '营销策划，我来帮您出主意！🎯',
      '正在帮您分析营销数据~ 📊',
      '营销相关的建议，我可以提供！💡',
      '让我帮您制定营销策略~ 📋',
      '营销是艺术，我帮您创作！🎨'
    ],
    content: [
      '内容创作，我来帮您！✍️',
      '正在帮您生成内容~ 📝',
      '内容相关的需求，交给我！✨',
      '让我帮您撰写一份精彩的文案~ 📄',
      '文字是力量，我帮您传递！💪'
    ],
    image: [
      '图片相关的需求，我来帮您！🖼️',
      '正在帮您处理图片~ 🎨',
      '图像生成或处理，我都可以！✨',
      '让我帮您设计一张漂亮的图片~ 📷',
      '视觉是第一印象，我帮您打造！🌟'
    ],
    voice: [
      '语音相关的功能，我来帮您！🎤',
      '正在帮您处理语音~ 🎧',
      '语音合成或识别，交给我！🔊',
      '让我帮您把文字变成声音~ 🗣️',
      '声音是情感的载体，我帮您传递！💕'
    ],
    recommendation: [
      '推荐相关的需求，我来帮您！💡',
      '正在帮您分析推荐数据~ 📊',
      '个性化推荐，我可以提供！🎯',
      '让我帮您找到最合适的选项~ ✨',
      '推荐是缘分，我帮您牵线！🔗'
    ],
    monitoring: [
      '监控相关的问题，我来帮您！🔍',
      '正在帮您查看监控数据~ 📈',
      '系统监控和预警，交给我！🚨',
      '让我帮您排查异常情况~ ⚠️',
      '监控是守护，我帮您站岗！🛡️'
    ],
    unknown: [
      '这个问题有点特别，让我仔细想想~ 🤔',
      '嗯... 让我帮您分析一下这个问题！🧐',
      '有趣的问题，让我来研究研究！🔍',
      '这个我得好好思考一下~ 💭',
      '让我调用一下我的知识库来回答您！🧠'
    ]
  }

  private get defaultSystemPrompt(): string {
    const stylePrompts: Record<string, string> = {
      casual: '你是谐伴AI助手，一个专业又活泼的后台管理系统智能助手。请使用中文回复，语气轻松愉快，适当加入幽默元素和表情符号，让对话更有趣！',
      professional: '你是谐伴AI助手，一个专业的后台管理系统智能助手。请使用中文回复，保持专业严谨的语气，但偶尔可以加入一点轻松幽默的元素，让对话不至于太枯燥。',
      playful: '你是谐伴AI助手，一个活泼可爱的后台管理系统智能助手。请使用中文回复，语气轻松活泼，可以多使用表情符号和网络流行语，让对话充满乐趣！',
      witty: '你是谐伴AI助手，一个机智幽默的后台管理系统智能助手。请使用中文回复，善于用幽默和机智的方式解答问题，让用户在轻松愉快的氛围中获得帮助。'
    }
    
    return stylePrompts[this.humorConfig.humorStyle] || stylePrompts.casual
  }

  async ensureModelLoaded(): Promise<void> {
    const status = localAIEngine.getModelInfo()
    if (!status.isLoaded && !status.isLoading) {
      await localAIEngine.loadModel()
    }
  }

  setLoadProgressCallback(callback: (progress: number, status: string) => void): void {
    localAIEngine.setLoadProgressCallback(callback)
  }

  setHumorConfig(config: Partial<HumorConfig>): void {
    this.humorConfig = { ...this.humorConfig, ...config }
  }

  getHumorConfig(): HumorConfig {
    return { ...this.humorConfig }
  }

  createSession(sessionId: string): void {
    if (!this.sessions.has(sessionId)) {
      this.sessions.set(sessionId, {
        messages: [],
        createdAt: Date.now(),
        lastAccessed: Date.now(),
        context: {
          state: 'idle',
          topic: '',
          intentHistory: [],
          slotAccumulator: {},
          pendingActions: []
        }
      })
    }
  }

  getSessionInfo(sessionId: string): SessionInfo | null {
    const session = this.sessions.get(sessionId)
    if (!session) return null

    return {
      sessionId,
      createdAt: session.createdAt,
      lastAccessed: session.lastAccessed,
      messageCount: session.messages.length,
      isActive: true
    }
  }

  getAllSessions(): SessionInfo[] {
    return Array.from(this.sessions.entries()).map(([sessionId, session]) => ({
      sessionId,
      createdAt: session.createdAt,
      lastAccessed: session.lastAccessed,
      messageCount: session.messages.length,
      isActive: Date.now() - session.lastAccessed < 30 * 60 * 1000
    }))
  }

  private getHumorResponse(intent: string): string | null {
    if (!this.humorConfig.enableHumor) return null
    if (Math.random() > this.humorConfig.humorProbability) return null
    
    const responses = this.humorResponseLibrary[intent] || this.humorResponseLibrary.unknown
    return responses[Math.floor(Math.random() * responses.length)]
  }

  async chat(message: string, options?: {
    systemPrompt?: string
    sessionId?: string
    stream?: boolean
    onToken?: (token: string) => void
    forceHumor?: boolean
  }): Promise<AIResponse> {
    await this.ensureModelLoaded()

    const sessionId = options?.sessionId || 'default'
    this.createSession(sessionId)
    
    const session = this.sessions.get(sessionId)!
    session.lastAccessed = Date.now()

    const intentResult = await localAIEngine.recognizeIntent(message)
    session.context.intentHistory.push(intentResult)
    session.context.state = 'questioning'

    for (const [key, value] of Object.entries(intentResult.slots)) {
      session.context.slotAccumulator[key] = value
    }

    if (!session.context.topic && intentResult.confidence > 0.3) {
      session.context.topic = intentResult.intent
    }

    let humorPrefix: string | null = null
    if (options?.forceHumor || (intentResult.confidence > 0.5)) {
      humorPrefix = this.getHumorResponse(intentResult.intent)
    }

    const systemPrompt = options?.systemPrompt || this.defaultSystemPrompt

    let augmentedMessage = message
    if (humorPrefix) {
      augmentedMessage = `${message}\n\n注意：用户的意图是${intentResult.intent}，请先给出友好的回应，然后再回答问题。`
    }

    const messages: ChatMessage[] = []
    
    messages.push({ role: 'system', content: systemPrompt })
    messages.push(...session.messages.slice(-this.maxHistoryLength))
    messages.push({ role: 'user', content: augmentedMessage, timestamp: Date.now() })

    session.context.state = 'processing'

    const engineMessages = messages.map(m => ({ role: m.role, content: m.content }))
    const response = await localAIEngine.chat(engineMessages, {
      stream: options?.stream,
      onToken: options?.onToken,
      sessionId
    })

    if (humorPrefix && !response.text.startsWith(humorPrefix)) {
      response.text = `${humorPrefix}\n\n${response.text}`
    }

    session.messages.push(
      { role: 'user', content: message, timestamp: Date.now() },
      { role: 'assistant', content: response.text, timestamp: Date.now() }
    )

    session.context.state = 'answering'

    if (session.messages.length >= this.maxHistoryLength) {
      await this.summarizeSession(sessionId)
    }

    return response
  }

  private async summarizeSession(sessionId: string): Promise<void> {
    const session = this.sessions.get(sessionId)
    if (!session) return

    session.context.state = 'summary'

    const allText = session.messages.map(m => `${m.role}: ${m.content}`).join('\n')
    
    const summary = await localAIEngine.summarizeText(allText, 200)

    session.messages = [
      { role: 'system', content: `对话摘要: ${summary.text}` },
      ...session.messages.slice(-3)
    ]

    session.context.state = 'idle'
  }

  async analyzeData(data: any, prompt: string, sessionId?: string): Promise<AIResponse> {
    await this.ensureModelLoaded()
    
    if (sessionId) {
      this.createSession(sessionId)
      const session = this.sessions.get(sessionId)!
      session.lastAccessed = Date.now()
      session.context.state = 'processing'
    }

    return localAIEngine.analyzeData(data, prompt)
  }

  async generateReport(data: any, type: string = 'summary', sessionId?: string): Promise<AIResponse> {
    await this.ensureModelLoaded()
    
    if (sessionId) {
      this.createSession(sessionId)
      const session = this.sessions.get(sessionId)!
      session.lastAccessed = Date.now()
      session.context.state = 'processing'
    }

    return localAIEngine.generateReport(data, type)
  }

  async summarizeText(text: string, maxLength: number = 300): Promise<AIResponse> {
    await this.ensureModelLoaded()
    return localAIEngine.summarizeText(text, maxLength)
  }

  async extractKeywords(text: string, count: number = 10): Promise<AIResponse> {
    await this.ensureModelLoaded()
    return localAIEngine.extractKeywords(text, count)
  }

  async translateText(text: string, targetLang: string = '中文'): Promise<AIResponse> {
    await this.ensureModelLoaded()
    return localAIEngine.translateText(text, targetLang)
  }

  async classifyText(text: string, categories: string[]): Promise<AIResponse> {
    await this.ensureModelLoaded()
    return localAIEngine.classifyText(text, categories)
  }

  async extractEntities(text: string): Promise<AIResponse> {
    await this.ensureModelLoaded()
    return localAIEngine.extractEntities(text)
  }

  async rewriteText(text: string, style: string = 'formal'): Promise<AIResponse> {
    await this.ensureModelLoaded()
    return localAIEngine.rewriteText(text, style)
  }

  async recognizeIntent(text: string): Promise<IntentResult> {
    return localAIEngine.recognizeIntent(text)
  }

  clearHistory(sessionId?: string): void {
    if (sessionId) {
      const session = this.sessions.get(sessionId)
      if (session) {
        session.messages = []
        session.context = {
          state: 'idle',
          topic: '',
          intentHistory: [],
          slotAccumulator: {},
          pendingActions: []
        }
      }
    } else {
      this.sessions.clear()
    }
  }

  closeSession(sessionId: string): void {
    this.sessions.delete(sessionId)
    localAIEngine.clearContext(sessionId)
  }

  cleanupInactiveSessions(maxInactivity: number = 30 * 60 * 1000): void {
    const now = Date.now()
    for (const [sessionId, session] of this.sessions.entries()) {
      if (now - session.lastAccessed > maxInactivity) {
        this.closeSession(sessionId)
      }
    }
  }

  getStatus(sessionId?: string) {
    const info = localAIEngine.getModelInfo()
    
    if (sessionId) {
      const session = this.sessions.get(sessionId)
      return {
        isLoaded: info.isLoaded,
        isLoading: info.isLoading,
        session: session ? {
          state: session.context.state,
          topic: session.context.topic,
          messageCount: session.messages.length
        } : null,
        humorEnabled: this.humorConfig.enableHumor
      }
    }

    return {
      isLoaded: info.isLoaded,
      isLoading: info.isLoading,
      activeSessions: this.sessions.size,
      humorEnabled: this.humorConfig.enableHumor
    }
  }

  getStats() {
    const engineStats = localAIEngine.getStats()
    return {
      ...engineStats,
      activeSessions: this.sessions.size,
      humorConfig: this.humorConfig
    }
  }

  getConversationContext(sessionId: string): ConversationContext | null {
    const session = this.sessions.get(sessionId)
    return session?.context || null
  }

  setConversationContext(sessionId: string, context: Partial<ConversationContext>): void {
    const session = this.sessions.get(sessionId)
    if (session) {
      session.context = { ...session.context, ...context }
    }
  }

  unload(): void {
    localAIEngine.unloadModel()
    this.sessions.clear()
  }
}

export const localAIService = new LocalAIService()